import { useState, useEffect, useCallback, useRef } from "react";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Select } from "../components/ui/select";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow
} from "../components/ui/table";
import { Play, Square, Clock, CalendarDays, LogIn, LogOut, Loader2, Timer, Umbrella, AlarmClock } from "lucide-react";
import { toast } from "sonner";
import { LiveClock } from "../components/workSession/LiveClock";
import {
    clockIn as apiClockIn,
    clockOut as apiClockOut,
    getMyStatus,
    getMyMonthly
} from "../services/crmServices/workSessionService";

function fmtDuration(totalSeconds) {
    const s = Math.max(0, Math.floor(totalSeconds || 0));
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    const sec = s % 60;
    return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(sec).padStart(2, "0")}`;
}

function fmtHours(totalSeconds) {
    const h = (totalSeconds || 0) / 3600;
    return `${(Math.round(h * 100) / 100).toFixed(2)}h`;
}

function fmtTime(iso, tz) {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleTimeString("en-GB", {
            timeZone: tz,
            hour: "2-digit",
            minute: "2-digit"
        });
    } catch {
        return new Date(iso).toLocaleTimeString();
    }
}

function fmtDateLabel(dateStr) {
    // dateStr is "YYYY-MM-DD" — render as e.g. "Mon 27 Jul"
    try {
        const [y, m, d] = dateStr.split("-").map((n) => parseInt(n, 10));
        const dt = new Date(Date.UTC(y, m - 1, d));
        return dt.toLocaleDateString("en-GB", {
            timeZone: "UTC",
            weekday: "short",
            day: "2-digit",
            month: "short"
        });
    } catch {
        return dateStr;
    }
}

function buildMonthOptions() {
    const opts = [];
    const now = new Date();
    for (let i = 0; i < 12; i++) {
        const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
        const value = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
        const label = d.toLocaleDateString("en-GB", { month: "long", year: "numeric" });
        opts.push({ value, label });
    }
    return opts;
}

const StatCard = ({ icon: Icon, label, value, hint }) => (
    <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4 flex items-center gap-3">
        <div className="h-10 w-10 rounded-lg bg-red-50 flex items-center justify-center flex-shrink-0">
            <Icon className="h-5 w-5 text-red-600" />
        </div>
        <div className="min-w-0">
            <div className="text-xs text-gray-500">{label}</div>
            <div className="text-lg font-semibold text-gray-900 leading-tight">{value}</div>
            {hint && <div className="text-[11px] text-gray-400">{hint}</div>}
        </div>
    </div>
);

export function MyWork() {
    const monthOptions = buildMonthOptions();
    const [status, setStatus] = useState(null);
    const [monthly, setMonthly] = useState(null);
    const [month, setMonth] = useState(monthOptions[0].value);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [nowTick, setNowTick] = useState(Date.now());
    const startRef = useRef(null);

    const loadStatus = useCallback(async () => {
        try {
            const res = await getMyStatus();
            setStatus(res?.data || null);
            startRef.current = res?.data?.session?.startedAt || null;
        } catch (e) {
            // silent; toast on action instead
        }
    }, []);

    const loadMonthly = useCallback(async (m) => {
        try {
            const res = await getMyMonthly(m);
            setMonthly(res?.data || null);
        } catch (e) {
            toast.error("Could not load your monthly stats.");
        }
    }, []);

    useEffect(() => {
        (async () => {
            setLoading(true);
            await Promise.all([loadStatus(), loadMonthly(month)]);
            setLoading(false);
        })();
    }, []);

    useEffect(() => {
        loadMonthly(month);
    }, [month, loadMonthly]);

    // Live ticking clock while working.
    const isWorking = !!status?.isWorking;
    useEffect(() => {
        if (!isWorking) return;
        const t = setInterval(() => setNowTick(Date.now()), 1000);
        return () => clearInterval(t);
    }, [isWorking]);

    const liveSeconds = (() => {
        if (!isWorking || !startRef.current) return status?.session?.elapsedSeconds || 0;
        return Math.max(0, Math.floor((nowTick - new Date(startRef.current).getTime()) / 1000));
    })();

    const handleClockIn = async () => {
        setBusy(true);
        try {
            await apiClockIn();
            toast.success("Clocked in. Have a great shift!");
            await Promise.all([loadStatus(), loadMonthly(month)]);
        } catch (e) {
            toast.error(e?.response?.data?.message || "Could not clock in.");
        } finally {
            setBusy(false);
        }
    };

    const handleClockOut = async () => {
        setBusy(true);
        try {
            await apiClockOut();
            toast.success("Clocked out. See you next time!");
            await Promise.all([loadStatus(), loadMonthly(month)]);
        } catch (e) {
            toast.error(e?.response?.data?.message || "Could not clock out.");
        } finally {
            setBusy(false);
        }
    };

    const tz = status?.timezone || monthly?.timezone;
    const todaySession = status?.session;
    const alreadyEndedToday = todaySession && todaySession.endedAt && !isWorking;

    return (
        <div className="p-4 md:p-6 max-w-5xl mx-auto">
            <div className="mb-6 flex items-start justify-between gap-4 flex-wrap">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900">My Work</h1>
                    <p className="text-sm text-gray-500">
                        Clock in when you start your shift and clock out when you leave.
                        {status?.schedule ? ` Work hours: ${status.schedule.start}–${status.schedule.end}.` : ""}
                    </p>
                </div>
                <LiveClock timezone={tz} />
            </div>

            {loading ? (
                <div className="flex items-center justify-center py-24 text-gray-400">
                    <Loader2 className="h-6 w-6 animate-spin mr-2" /> Loading…
                </div>
            ) : (
                <>
                    {/* Clock in/out hero */}
                    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 mb-6">
                        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                            <div className="flex items-center gap-4">
                                <div
                                    className={
                                        "h-14 w-14 rounded-full flex items-center justify-center " +
                                        (isWorking ? "bg-green-100" : "bg-gray-100")
                                    }
                                >
                                    <Timer
                                        className={"h-7 w-7 " + (isWorking ? "text-green-600" : "text-gray-400")}
                                    />
                                </div>
                                <div>
                                    <div className="flex items-center gap-2">
                                        <span className="text-lg font-semibold text-gray-900">
                                            {isWorking ? "You're working" : "Not clocked in"}
                                        </span>
                                        {isWorking && (
                                            <Badge className="bg-green-100 text-green-700">Live</Badge>
                                        )}
                                        {alreadyEndedToday && (
                                            <Badge className="bg-blue-100 text-blue-700">Shift complete</Badge>
                                        )}
                                        {status?.lateToday && (
                                            <Badge className="bg-red-100 text-red-700">
                                                Late {status.lateMinutesToday}m
                                            </Badge>
                                        )}
                                    </div>
                                    <div className="text-sm text-gray-500">
                                        {isWorking && todaySession
                                            ? `Started at ${fmtTime(todaySession.startedAt, tz)}`
                                            : alreadyEndedToday
                                            ? `Today: ${fmtTime(todaySession.startedAt, tz)} – ${fmtTime(
                                                  todaySession.endedAt,
                                                  tz
                                              )}`
                                            : "Click Start when you begin your shift."}
                                    </div>
                                </div>
                            </div>

                            <div className="flex items-center gap-4">
                                {isWorking && (
                                    <div className="text-right">
                                        <div className="text-xs text-gray-400">Elapsed today</div>
                                        <div className="text-2xl font-bold text-gray-900 tabular-nums">
                                            {fmtDuration(liveSeconds)}
                                        </div>
                                    </div>
                                )}
                                {isWorking ? (
                                    <Button
                                        size="lg"
                                        onClick={handleClockOut}
                                        disabled={busy}
                                        className="bg-red-600 hover:bg-red-700"
                                    >
                                        {busy ? (
                                            <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                                        ) : (
                                            <LogOut className="h-5 w-5 mr-2" />
                                        )}
                                        End shift
                                    </Button>
                                ) : (
                                    <Button
                                        size="lg"
                                        onClick={handleClockIn}
                                        disabled={busy || alreadyEndedToday}
                                        className="bg-green-600 hover:bg-green-700"
                                        title={alreadyEndedToday ? "You've already completed today's shift" : ""}
                                    >
                                        {busy ? (
                                            <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                                        ) : (
                                            <LogIn className="h-5 w-5 mr-2" />
                                        )}
                                        Start shift
                                    </Button>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Month selector */}
                    <div className="flex items-center justify-between mb-3">
                        <h2 className="text-lg font-semibold text-gray-900">Monthly statistics</h2>
                        <div className="w-48">
                            <Select options={monthOptions} value={month} onChange={setMonth} />
                        </div>
                    </div>

                    {/* Summary cards */}
                    <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-5">
                        <StatCard
                            icon={Clock}
                            label="Total hours"
                            value={fmtHours(monthly?.totalSeconds || 0)}
                        />
                        <StatCard
                            icon={CalendarDays}
                            label="Days worked"
                            value={monthly?.daysWorked || 0}
                        />
                        <StatCard
                            icon={AlarmClock}
                            label="Late days"
                            value={monthly?.lateDays || 0}
                            hint={monthly?.avgLateMinutes ? `avg +${monthly.avgLateMinutes}m` : undefined}
                        />
                        <StatCard
                            icon={Play}
                            label="Avg start"
                            value={monthly?.avgStart || "—"}
                        />
                        <StatCard
                            icon={Square}
                            label="Avg end"
                            value={monthly?.avgEnd || "—"}
                        />
                    </div>

                    {/* Day-offs */}
                    {monthly?.dayOffSummary && (
                        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4 mb-5">
                            <div className="flex items-center gap-2 mb-3">
                                <Umbrella className="h-4 w-4 text-sky-600" />
                                <span className="text-sm font-semibold text-gray-800">
                                    Day-offs —{" "}
                                    {monthOptions.find((o) => o.value === (monthly.dayOffSummary.month || month))?.label ||
                                        monthly.dayOffSummary.month}
                                </span>
                            </div>
                            <div className="grid grid-cols-3 gap-3 mb-3">
                                <div className="bg-gray-50 rounded-lg p-3 text-center">
                                    <div className="text-xs text-gray-500">Allowance</div>
                                    <div className="text-lg font-bold text-gray-900">
                                        {monthly.dayOffSummary.allowance}
                                    </div>
                                </div>
                                <div className="bg-gray-50 rounded-lg p-3 text-center">
                                    <div className="text-xs text-gray-500">Used</div>
                                    <div className="text-lg font-bold text-gray-900">
                                        {monthly.dayOffSummary.used}
                                    </div>
                                </div>
                                <div className="bg-gray-50 rounded-lg p-3 text-center">
                                    <div className="text-xs text-gray-500">Remaining</div>
                                    <div
                                        className={
                                            "text-lg font-bold " +
                                            (monthly.dayOffSummary.remaining < 0
                                                ? "text-red-600"
                                                : monthly.dayOffSummary.remaining <= 3
                                                ? "text-amber-600"
                                                : "text-green-600")
                                        }
                                    >
                                        {monthly.dayOffSummary.remaining}
                                    </div>
                                </div>
                            </div>
                            {(monthly.dayOffSummary.dayOffs || []).length > 0 && (
                                <div className="flex flex-wrap gap-2">
                                    {monthly.dayOffSummary.dayOffs.map((d) => (
                                        <Badge key={d._id} className="bg-sky-50 text-sky-700 font-normal">
                                            {d.startDate === d.endDate
                                                ? fmtDateLabel(d.startDate)
                                                : `${fmtDateLabel(d.startDate)} – ${fmtDateLabel(d.endDate)}`}
                                            {d.note ? ` · ${d.note}` : ""}
                                        </Badge>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    {/* Per-day table */}
                    <h2 className="text-lg font-semibold text-gray-900 mb-3">Daily log</h2>
                    <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>Day</TableHead>
                                    <TableHead>Start</TableHead>
                                    <TableHead>End</TableHead>
                                    <TableHead className="text-right">Duration</TableHead>
                                    <TableHead className="text-center">Notes</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {(monthly?.days || []).length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={5} className="text-center text-gray-400 py-8">
                                            No work recorded this month.
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    [...(monthly?.days || [])]
                                        .sort((a, b) => (a.date < b.date ? 1 : -1))
                                        .map((d) => (
                                            <TableRow key={d._id || d.date}>
                                                <TableCell className="font-medium text-gray-800">
                                                    {fmtDateLabel(d.date)}
                                                </TableCell>
                                                <TableCell>{fmtTime(d.startedAt, tz)}</TableCell>
                                                <TableCell>
                                                    {d.active ? (
                                                        <span className="text-green-600 font-medium">In progress</span>
                                                    ) : (
                                                        fmtTime(d.endedAt, tz)
                                                    )}
                                                </TableCell>
                                                <TableCell className="text-right tabular-nums">
                                                    {fmtDuration(d.durationSeconds)}
                                                </TableCell>
                                                <TableCell className="text-center space-x-1">
                                                    {d.late && (
                                                        <Badge className="bg-red-100 text-red-700">
                                                            Late {d.lateMinutes}m
                                                        </Badge>
                                                    )}
                                                    {d.earlyLeave && (
                                                        <Badge className="bg-orange-100 text-orange-700">
                                                            Left early {d.earlyMinutes}m
                                                        </Badge>
                                                    )}
                                                    {d.autoClosed && (
                                                        <Badge className="bg-amber-100 text-amber-700">Auto-closed</Badge>
                                                    )}
                                                    {d.createdByAdmin && !d.autoClosed && (
                                                        <Badge className="bg-purple-100 text-purple-700">Adjusted</Badge>
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                        ))
                                )}
                            </TableBody>
                        </Table>
                    </div>
                </>
            )}
        </div>
    );
}

export default MyWork;
